// A centered dialog over a dimmed scrim, portaled to <body> so it escapes any
// overflow/stacking context of the form it was opened from. Dismissed by the ✕,
// Escape, or a press on the scrim; the page behind does not scroll while open.
// Focus moves into the dialog on open, Tab is trapped inside it, and on close
// focus returns to whatever opened it — or, when the opener unmounted with the
// dialog (e.g. a picker that disappears once its slot is filled), to the
// nearest ancestor of it still on the page.

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation('common');
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const openerAncestors: HTMLElement[] = [];
    for (let el = opener?.parentElement; el && el !== document.body; el = el.parentElement) {
      openerAncestors.push(el);
    }
    const node = dialogRef.current;
    const target = node
      ?.querySelector('.modal__body')
      ?.querySelector<HTMLElement>(FOCUSABLE);
    (target ?? node)?.focus();

    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = priorOverflow;
      if (opener?.isConnected) {
        opener.focus();
        return;
      }
      const fallback = openerAncestors.find((el) => el.isConnected);
      if (!fallback) return;
      if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
      fallback.focus({ preventScroll: true });
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const node = dialogRef.current;
    if (!node) return;
    const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div
      className="modal-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="card modal"
        onKeyDown={onKeyDown}
      >
        <div className="modal__header">
          <h3 id={titleId} className="modal__title">
            {title}
          </h3>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label={t('actions.close')}
          >
            ✕
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
